import type {
  Airport,
  Comparison,
  FoundFlight,
  Mode,
  Plan,
  PlanStop,
  Point,
  Road,
  RoutePath,
  RouteSearch,
  TripView,
  Variant,
} from '@trippath/shared';

import { alongRoad, wayAsk, wayKey, type Way } from './roads';

/** The roads known, by [`roadKey`]. */
export type Roads = ReadonlyMap<string, Road | null>;

/** Whether a way may go along a road: by bus or car, or by ferry along its line. */
export function byRoad(mode: Mode | null | undefined): boolean {
  // A train is drawn along its rails, never along a road.
  // A way on foot without its own track goes along the paths, as a walk in a town does.
  return (
    mode === 'bus' || mode === 'car' || mode === 'ferry' || mode === 'train' || mode === 'foot'
  );
}

/** A way from `from` to `to`: along its road when it goes by one that is known, else the arc. */
function way(from: Point, to: Point, mode: Mode | null | undefined, roads: Roads) {
  const road = alongRoad(mode, roads.get(wayKey(from, to, mode)));
  return road ? road.path : drawn(from, to, mode);
}

/**
 * How a line between two places is drawn: by its way; dashed when not known, not direct, or a
 * path not chosen.
 */
export type LineKind =
  | Mode
  | 'travel'
  | 'change'
  | 'other'
  | 'draft'
  | 'variant'
  | 'walk'
  | 'metro'
  | 'tram'
  | 'taxi'
  | 'bike'
  | 'cable';

/** A place on the map. `guessed` is placed by its airport; `fix` is a "did you mean". */
export interface MapPoint extends Point {
  label: string;
  kind: 'stop' | 'guessed' | 'fix' | 'end' | 'stay' | 'dock';
  /** The place's name when it is clicked, when the label says more: `KUL Kuala Lumpur`. */
  name?: string;
  /** The exact place a stop is at, under its name: `Tartu Airport`. */
  spot?: string;
  /** What it is on the page that drew it, told when it is pressed: a thing of a day, a room. */
  key?: string;
  /** A room's mark by its kind of place: `hotel`, `hostel`; none, a bed. A bike station's, how
   * many bikes it has now. */
  mark?: string;
}

/** A line between two places, as `[lon, lat]` pairs along the great circle. */
export interface MapLine {
  kind: LineKind;
  path: [number, number][];
  /** The stop of the plan it leaves, to open it when the line is pressed. */
  stop?: string;
  /** What it is, shown as it is pointed at: `Bus Riga → Bauska`. */
  title?: string;
  /** One part of a way with changes, picked apart from the rest of it: `Palace#1`. */
  part?: string;
  /** Each point's height in metres, as its own track's file said: drawn by it, light low and
   * dark high. */
  heights?: readonly (number | null)[];
}

/** What the map draws. */
/** A place of one's own on the map: its tag's emoji in a ring of its colour; places close
 * together gathered into one circle with a number. */
export interface MapPin extends Point {
  /** Told when it is pressed. */
  key: string;
  label: string;
  emoji: string;
  /** `#rrggbb`. */
  colour: string;
  /** Been to: drawn faded. */
  faded?: boolean;
  /** The one chosen on the page: standing out. */
  chosen?: boolean;
  /** Moved by dragging it: a way's anchor. */
  draggable?: boolean;
}

/** Where one has been: cells of a grid, each with how many points fell in it. */
export interface Explored {
  /** A cell's side, in degrees. */
  cell: number;
  /** Each cell's middle `[lon, lat]` and its count. */
  cells: [number, number, number][];
  /** All the rest under fog, else a heatmap. */
  fog: boolean;
}

export interface MapData {
  points: MapPoint[];
  lines: MapLine[];
  /** Places of one's own, gathered when close together. */
  pins?: MapPin[];
  /** Where one has been, as a heatmap or with the rest under fog. */
  explored?: Explored;
}

const RAD = Math.PI / 180;

/** How far apart two points are on a round Earth, in kilometres. */
function roundKm(a: Point, b: Point): number {
  const half =
    Math.sin(((b.lat - a.lat) * RAD) / 2) ** 2 +
    Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(((b.lon - a.lon) * RAD) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(half)));
}

/**
 * How far apart two points are, in kilometres, on the shortest way over the Earth as it is,
 * flattened at the poles: Vincenty's formula on the WGS-84 ellipsoid, as the server and Air Miles
 * Calculator measure a flight (SYD to AUH 12,058 km, where a round Earth says 12,062). Two points
 * nearly opposite, where it does not settle, the round Earth's.
 */
export function kmApart(a: Point, b: Point): number {
  const major = 6378137;
  const flat = 1 / 298.257223563;
  const minor = major * (1 - flat);
  const l = (b.lon - a.lon) * RAD;
  const u1 = Math.atan((1 - flat) * Math.tan(a.lat * RAD));
  const u2 = Math.atan((1 - flat) * Math.tan(b.lat * RAD));
  const [sinU1, cosU1, sinU2, cosU2] = [Math.sin(u1), Math.cos(u1), Math.sin(u2), Math.cos(u2)];
  let lambda = l;
  for (let turn = 0; turn < 200; turn += 1) {
    const [sinL, cosL] = [Math.sin(lambda), Math.cos(lambda)];
    const sinSigma = Math.hypot(cosU2 * sinL, cosU1 * sinU2 - sinU1 * cosU2 * cosL);
    if (sinSigma === 0) return 0;
    const cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosL;
    const sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinL) / sinSigma;
    const cos2Alpha = 1 - sinAlpha * sinAlpha;
    // On the equator the middle term is none.
    const cos2Sm = cos2Alpha === 0 ? 0 : cosSigma - (2 * sinU1 * sinU2) / cos2Alpha;
    const c = (flat / 16) * cos2Alpha * (4 + flat * (4 - 3 * cos2Alpha));
    const before = lambda;
    lambda =
      l +
      (1 - c) *
        flat *
        sinAlpha *
        (sigma + c * sinSigma * (cos2Sm + c * cosSigma * (-1 + 2 * cos2Sm * cos2Sm)));
    if (Math.abs(lambda - before) < 1e-12) {
      const uSq = (cos2Alpha * (major * major - minor * minor)) / (minor * minor);
      const bigA = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
      const bigB = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
      const delta =
        bigB *
        sinSigma *
        (cos2Sm +
          (bigB / 4) *
            (cosSigma * (-1 + 2 * cos2Sm * cos2Sm) -
              (bigB / 6) *
                cos2Sm *
                (-3 + 4 * sinSigma * sinSigma) *
                (-3 + 4 * cos2Sm * cos2Sm)));
      return (minor * bigA * (sigma - delta)) / 1000;
    }
  }
  return roundKm(a, b);
}

/**
 * The great circle from `from` to `to`, a point every two degrees, as `[lon, lat]`. Longitudes
 * run on past ±180 rather than jump back, so a line over the Pacific stays one line.
 */
export function arc(from: Point, to: Point): [number, number][] {
  const [p1, l1, p2, l2] = [from.lat * RAD, from.lon * RAD, to.lat * RAD, to.lon * RAD];
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((p2 - p1) / 2) ** 2 +
          Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2,
      ),
    );
  if (Math.sin(d) < 1e-9) {
    return [
      [from.lon, from.lat],
      [to.lon, to.lat],
    ];
  }
  const steps = Math.ceil(d / (2 * RAD));
  const path: [number, number][] = [];
  let previous = from.lon;
  for (let step = 0; step <= steps; step++) {
    const f = step / steps;
    const a = Math.sin((1 - f) * d) / Math.sin(d);
    const b = Math.sin(f * d) / Math.sin(d);
    const x = a * Math.cos(p1) * Math.cos(l1) + b * Math.cos(p2) * Math.cos(l2);
    const y = a * Math.cos(p1) * Math.sin(l1) + b * Math.cos(p2) * Math.sin(l2);
    const z = a * Math.sin(p1) + b * Math.sin(p2);
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y)) / RAD;
    const raw = Math.atan2(y, x) / RAD;
    const lon = raw - 360 * Math.round((raw - previous) / 360);
    previous = lon;
    path.push([lon, lat]);
  }
  return path;
}

/** Degrees of latitude as the map draws them, up and down: its Mercator y. */
export const mercator = (lat: number) =>
  Math.log(Math.tan(Math.PI / 4 + (lat * RAD) / 2)) / RAD;
const latitude = (y: number) => (2 * Math.atan(Math.exp(y * RAD)) - Math.PI / 2) / RAD;

/**
 * A flight as a map draws one, as `[lon, lat]` pairs: a gentle bow toward the pole, so even a
 * short hop reads as flown, not driven, and flights crossing each other do not sag into one.
 */
export function bow(from: Point, to: Point): [number, number][] {
  const { length } = bowOf(from, to);
  if (length < 1e-9) {
    return [
      [from.lon, from.lat],
      [to.lon, to.lat],
    ];
  }
  const steps = Math.max(16, Math.ceil(length));
  return Array.from({ length: steps + 1 }, (_, step) => bowAt(from, to, step / steps));
}

/** A flight's bow: its ends and the point it bends toward, in degrees as the map draws them. */
function bowOf(from: Point, to: Point) {
  // The short way round, across the date line when that is shorter.
  const toLon = to.lon - 360 * Math.round((to.lon - from.lon) / 360);
  const [x1, y1, x2, y2] = [from.lon, mercator(from.lat), toLon, mercator(to.lat)];
  const [dx, dy] = [x2 - x1, y2 - y1];
  // Toward the pole, as a great circle bends and as maps draw flights: up north of the equator,
  // down south of it; a flight due north or south bows to its left.
  const pole = y1 + y2 >= 0 ? 1 : -1;
  const side = (dx === 0 ? 1 : Math.sign(dx) * pole) * 0.15;
  return {
    x1,
    y1,
    x2,
    y2,
    cx: (x1 + x2) / 2 - dy * side,
    cy: (y1 + y2) / 2 + dx * side,
    length: Math.hypot(dx, dy),
  };
}

/** The point a part of the way along a flight's bow, as `[lon, lat]`: where a plane is drawn. */
export function bowAt(from: Point, to: Point, part: number): [number, number] {
  const { x1, y1, x2, y2, cx, cy } = bowOf(from, to);
  const [a, b, c] = [(1 - part) ** 2, 2 * (1 - part) * part, part ** 2];
  return [a * x1 + b * cx + c * x2, latitude(a * y1 + b * cy + c * y2)];
}

/** A way with no road to follow: a flight bowed as it is flown, the rest along the great circle. */
function drawn(from: Point, to: Point, mode: Mode | 'travel' | null | undefined) {
  return mode === 'flight' ? bow(from, to) : arc(from, to);
}

/** The ways by road between the stops of a plan, to ask the roads of. */
export function planRoads(plan: Plan): Way[] {
  return plan.stops.flatMap((stop, at) => {
    const ends = endsOf(stop, plan.stops[at + 1]);
    // A way with its own track is drawn along it: no road to ask.
    return ends && byRoad(stop.out?.mode) && stop.track === null
      ? [wayAsk(ends.from, ends.to, stop.out?.mode)]
      : [];
  });
}

/** Where a stop's way on is drawn from and to: its own two ends, else the two stops. */
function endsOf(
  stop: PlanStop,
  next: PlanStop | undefined,
): { from: Point; to: Point; next: PlanStop } | null {
  if (next === undefined) return null;
  const out = stop.out;
  const at = (p: Point): Point => ({ lat: p.lat, lon: p.lon });
  if (out?.from && out.to) return { from: at(out.from), to: at(out.to), next };
  return stop.place && next.place ? { from: at(stop.place), to: at(next.place), next } : null;
}

/** The trip's stops that have a place, and the way between each two in a row: along its road
 * when it goes by bus or car and the road is known. */
export function planMap(
  plan: Plan,
  roads: Roads = new Map(),
  /** What each way is, said as it is pointed at. */
  title?: (from: PlanStop, to: PlanStop) => string,
): MapData {
  // Where the ways really leave and arrive — an airport, a station — when not at the stop.
  const ends = new Map<string, MapPoint>();
  plan.stops.forEach((stop, at) => {
    const next = plan.stops[at + 1];
    if (next === undefined) return;
    for (const [end, of] of [
      [stop.out?.from, stop],
      [stop.out?.to, next],
    ] as const) {
      if (end && of.place && kmApart(end, of.place) > 0.5) {
        ends.set(`${end.lat},${end.lon}`, {
          lat: end.lat,
          lon: end.lon,
          label: end.name,
          kind: 'end',
        });
      }
    }
  });
  return {
    points: [
      ...plan.stops.flatMap((stop) =>
        stop.place
          ? [
              {
                lat: stop.place.lat,
                lon: stop.place.lon,
                label: stop.city,
                kind: stop.place.guessed ? ('guessed' as const) : ('stop' as const),
                ...(stop.place.name === null ? {} : { spot: stop.place.name }),
              },
            ]
          : [],
      ),
      ...ends.values(),
      // Each stay booked where it is.
      ...plan.stops.flatMap((stop) =>
        stop.bookings.flatMap((booking) =>
          booking.kind === 'stay' && booking.place
            ? [
                {
                  lat: booking.place.lat,
                  lon: booking.place.lon,
                  label: booking.name,
                  kind: 'stay' as const,
                  mark: booking.lodging ?? 'stay',
                  // Pressed, its booking is opened.
                  key: booking.id,
                },
              ]
            : [],
        ),
      ),
    ],
    lines: plan.stops.flatMap((stop, at) => {
      const ends = endsOf(stop, plan.stops[at + 1]);
      if (!ends) return [];
      // Its own track, from a file, as it was gone; else as worked out.
      const path = stop.track?.path ?? way(ends.from, ends.to, stop.out?.mode, roads);
      return [
        {
          kind: stop.out?.mode ?? 'travel',
          path,
          ...heightsOf(stop.out?.mode, path, stop.track?.heights),
          stop: stop.id,
          ...(title ? { title: title(stop, ends.next) } : {}),
        },
      ];
    }),
  };
}

/**
 * A flight's height along its way when its file says none, as an airliner flies: up to its
 * cruise in its first 200 km, down in its last 250, the cruise higher the longer it goes — a
 * short hop at 7 km, a long one at 11.
 */
export function flightHeights(path: readonly [number, number][]): number[] {
  const along = path.map(() => 0);
  path.forEach(([lon, lat], at) => {
    const before = path[at - 1];
    if (before) {
      along[at] =
        Number(along[at - 1]) + kmApart({ lat: before[1], lon: before[0] }, { lat, lon });
    }
  });
  const whole = Number(along.at(-1));
  const cruise = whole < 400 ? 7000 : whole < 1000 ? 9500 : 11_000;
  const [up, down] = [Math.min(whole * 0.4, 200), Math.min(whole * 0.45, 250)];
  return along.map((km) =>
    Math.round(
      Math.max(0, Math.min(cruise, (cruise * km) / up, (cruise * (whole - km)) / down)),
    ),
  );
}

/** A way's heights: its file's when it says any; a flight's as one flies when not. */
function heightsOf(
  mode: string | undefined,
  path: [number, number][],
  heights: readonly (number | null)[] | undefined,
): { heights?: readonly (number | null)[] } {
  if (heights?.some((height) => height !== null)) return { heights };
  return mode === 'flight' && path.length > 1 ? { heights: flightHeights(path) } : {};
}

/** A trip as a viewer sees it: the same places and ways as [`planMap`], without the money. */
export function viewMap(view: TripView): MapData {
  return {
    points: view.stops.flatMap((stop) =>
      stop.place
        ? [
            {
              lat: stop.place.lat,
              lon: stop.place.lon,
              label: stop.city,
              kind: stop.place.guessed ? ('guessed' as const) : ('stop' as const),
              ...(stop.place.name === null ? {} : { spot: stop.place.name }),
            },
          ]
        : [],
    ),
    lines: view.stops.flatMap((stop, at) => {
      const next = view.stops[at + 1]?.place;
      return stop.place && next
        ? [{ kind: stop.mode ?? 'travel', path: drawn(stop.place, next, stop.mode) }]
        : [];
    }),
  };
}

/**
 * A searched route's places, and its legs: flights solid when likely direct, dashed when not; a
 * leg by bus or car (`modes`, one per leg) along its road when it is known.
 */
export function routeMap(
  route: Pick<RouteSearch, 'parts' | 'legs'>,
  modes: readonly Mode[] = [],
  roads: Roads = new Map(),
): MapData {
  const points: MapPoint[] = [];
  for (const part of route.parts) {
    const place = part.place;
    if (place && !points.some((p) => p.lat === place.lat && p.lon === place.lon)) {
      points.push({
        lat: place.lat,
        lon: place.lon,
        label: place.town ? place.city : `${place.code} ${place.city}`,
        name: place.city,
        kind: part.kind === 'fix' ? 'fix' : 'stop',
      });
    }
  }
  return {
    points,
    lines: route.legs.map((leg, at) => ({
      kind: leg.direct ? 'flight' : 'change',
      path: way(leg.from, leg.to, modes[at], roads),
    })),
  };
}

/**
 * A variant kept: its new places, and each of its legs along its road when it goes by one, all
 * drawn as the variant.
 */
export function variantMap(variant: Variant, roads: Roads = new Map()): MapData {
  return {
    points: variant.places.slice(1, variant.rejoin ? -1 : undefined).map((place) => ({
      lat: place.lat,
      lon: place.lon,
      label: place.city,
      // Drawn as a place by its airport is: the town's own label stays.
      kind: 'guessed' as const,
    })),
    lines: pairs(variant.places).map(([from, to], at) => ({
      kind: 'variant' as const,
      path: way(from, to, variant.modes[at], roads),
    })),
  };
}

/**
 * Another trip of the family on the map: from where it parts, each of its stops with a place, and
 * each way between them by how it goes.
 */
export function kinMap(
  stops: readonly { id: string; city: string; place: Point | null; mode: Mode | null }[],
): MapData {
  // A branch's first stop is the town it parts at again: one point, the branch's own.
  const placed = stops
    .flatMap((stop) => (stop.place ? [{ ...stop, place: stop.place }] : []))
    .filter((stop, at, all) => all[at + 1]?.city !== stop.city);
  return {
    points: placed.map((stop) => ({
      lat: stop.place.lat,
      lon: stop.place.lon,
      label: stop.city,
      kind: 'stop' as const,
    })),
    // Each way pressed picks the stop it leaves.
    lines: pairs(placed).map(([from, to]) => ({
      kind: from.mode ?? 'travel',
      path: drawn(from.place, to.place, from.mode),
      stop: from.id,
    })),
  };
}

/** A line's two ends. */
function endsOfLine(line: MapLine): { from: Point; to: Point } | null {
  const first = line.path[0];
  const last = line.path.at(-1);
  return first && last
    ? { from: { lon: first[0], lat: first[1] }, to: { lon: last[0], lat: last[1] } }
    : null;
}

/** The ways by road of another trip's lines, to ask the roads of: a ferry, a bus, a train. */
export function kinRoads(kin: MapData | null): Way[] {
  return (kin?.lines ?? []).flatMap((line) => {
    const ends = endsOfLine(line);
    const mode = line.kind as Mode;
    return ends && byRoad(mode) ? [wayAsk(ends.from, ends.to, mode)] : [];
  });
}

/** Another trip's lines along their roads as they are known, as this trip's are. */
export function kinAlong(kin: MapData, roads: Roads): MapData {
  return {
    ...kin,
    lines: kin.lines.map((line) => {
      const ends = endsOfLine(line);
      const mode = line.kind as Mode;
      const road = ends && alongRoad(mode, roads.get(wayKey(ends.from, ends.to, mode)));
      return road ? { ...line, path: road.path } : line;
    }),
  };
}

/** The kinds of line drawn, in a fixed order, for the legend. */
/** How far the lines of each kind go, in kilometres, along their paths. */
export function lineKm(data: MapData): Map<LineKind, number> {
  const km = new Map<LineKind, number>();
  for (const line of data.lines) {
    let along = 0;
    line.path.forEach(([lon, lat], at) => {
      const before = line.path[at - 1];
      if (before) along += kmApart({ lat: before[1], lon: before[0] }, { lat, lon });
    });
    km.set(line.kind, (km.get(line.kind) ?? 0) + along);
  }
  return km;
}

export function lineKinds(data: MapData): LineKind[] {
  const order: LineKind[] = [
    'draft',
    'variant',
    'flight',
    'change',
    'train',
    'bus',
    'ferry',
    'car',
    'metro',
    'tram',
    'cable',
    'taxi',
    'bike',
    'walk',
    'travel',
    'other',
  ];
  return order.filter((kind) => data.lines.some((line) => line.kind === kind));
}

/** Each item with the one after it: `[a, b, c]` gives `[a, b]` and `[b, c]`. */
export function pairs<T>(items: readonly T[]): [T, T][] {
  const out: [T, T][] = [];
  items.reduce<T[]>((last, item) => {
    for (const before of last) out.push([before, item]);
    return [item];
  }, []);
  return out;
}

/** A flight's way as it is flown, through its changes. */
function flown(flight: FoundFlight): [number, number][] {
  return pairs([flight.from, ...flight.via, flight.to]).flatMap(([a, b]) => bow(a, b));
}

/**
 * The paths of a comparison: the one picked (by its flights, `0+2+3`) solid and drawn last, the
 * others dashed.
 */
export function pathsMap(
  compared: Comparison,
  shown: RoutePath[],
  picked: string | null,
): MapData {
  const flightsOf = (path: RoutePath) =>
    path.flights.flatMap((at) => compared.flights.slice(at, at + 1));
  const airports = new Map<string, Airport>();
  for (const flight of shown.flatMap(flightsOf)) {
    for (const airport of [flight.from, ...flight.via, flight.to]) {
      airports.set(airport.code, airport);
    }
  }
  const key = (path: RoutePath) => path.flights.join('+');
  const ordered = [
    ...shown.filter((path) => key(path) !== picked),
    ...shown.filter((path) => key(path) === picked),
  ];
  return {
    points: [...airports.values()].map((airport) => ({
      lat: airport.lat,
      lon: airport.lon,
      label: `${airport.code} ${airport.city}`,
      kind: 'stop' as const,
    })),
    lines: ordered.map((path) => ({
      kind: key(path) === picked ? ('flight' as const) : ('other' as const),
      path: flightsOf(path).flatMap(flown),
    })),
  };
}

/** How far apart two points are, enough to order them: degrees, the longitude shrunk. */
export function apart(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const x = (a.lon - b.lon) * Math.cos(((a.lat + b.lat) / 360) * Math.PI);
  return Math.hypot(x, a.lat - b.lat);
}
